import { useAIRovoButtonEnablement } from '@confluence/ai-common/entry-points/useAIRovoButtonEnablement.ts';
import { fg } from '@confluence/feature-gating';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useReadTime } from '@confluence/read-time/entry-points/useReadTime.ts';

/**
 * Hook to determine if nudge recommendation checks should be enabled
 *
 * @param contentId
 * @param contentType
 * @returns boolean indicating if nudge recommendation checks are enabled
 */
export const useNudgeRecommendationFilter = (contentType: string, contentId: string): boolean => {
	const { isAdminHubAIEnabled } = useSessionData();
	const pageMode = useGetPageMode();
	const isRovoButtonEnabled = useAIRovoButtonEnablement();

	const { readTime: readTimeMinutes, loading: readTimeLoading } = useReadTime(
		contentId,
		!isAdminHubAIEnabled || pageMode === PageMode.EDIT,
	);

	const minReadTimeMinutes = fg('rovo_button_proactive_nudge_min_read_time_increase') ? 1.0 : 0.5;

	/**
	 * Filters to prevent high request volume checking for nudge recommendations
	 * 1. Rovo Button must be enabled
	 * 2. Content type must be 'page' (TODO: extend to 'blogpost' when backend 500s are fixed)
	 * 3. Page must not be in edit mode
	 * 4. Read time query must be completed
	 * 5. Read time must be available and exceed the minimum threshold (1 min with gate on, 30s with gate off)
	 */
	return (
		isRovoButtonEnabled &&
		contentType === 'page' &&
		pageMode !== PageMode.EDIT &&
		!readTimeLoading &&
		Number(readTimeMinutes) > minReadTimeMinutes
	);
};
